/**
 * School Stats Schemas
 *
 * Types for periodic school statistics (daily, weekly, monthly).
 * Used by the compute-school-stats endpoint and consumed by CIO at send time.
 */

import { z } from 'zod'

// --- Enums ---

export const SchoolStatsPeriodSchema = z.enum(['daily', 'weekly', 'monthly'])
export type SchoolStatsPeriod = z.infer<typeof SchoolStatsPeriodSchema>

export const SchoolFeatureActivationStatusSchema = z.enum(['active', 'disabled', 'not_activated'])
export type SchoolFeatureActivationStatus = z.infer<typeof SchoolFeatureActivationStatusSchema>

// --- Stats Data ---

export const UserCountsByRoleSchema = z.object({
  total: z.number(),
  students: z.number(),
  parents: z.number(),
  faculty: z.number(),
  staff: z.number()
})

export type UserCountsByRole = z.infer<typeof UserCountsByRoleSchema>

export const BetaTestersSchema = z.object({
  total: z.number(),
  active: z.number()
})

export type BetaTesters = z.infer<typeof BetaTestersSchema>

export const ContentStatsSchema = z.object({
  published: z.number().optional(),
  posted: z.number().optional(),
  reactions: z.number(),
  comments: z.number()
})

export type ContentStats = z.infer<typeof ContentStatsSchema>

export const FeatureStatusesSchema = z.object({
  chat: SchoolFeatureActivationStatusSchema,
  feeds: SchoolFeatureActivationStatusSchema,
  mediaLibrary: SchoolFeatureActivationStatusSchema,
  directory: SchoolFeatureActivationStatusSchema
})

export type FeatureStatuses = z.infer<typeof FeatureStatusesSchema>

export const SchoolStatsDataSchema = z.object({
  addedUsers: UserCountsByRoleSchema,
  activeUsers: UserCountsByRoleSchema,
  pendingUsers: UserCountsByRoleSchema,
  betaTesters: BetaTestersSchema,
  platformContent: z.object({
    published: z.number(),
    reactions: z.number(),
    comments: z.number()
  }),
  userContent: z.object({
    posted: z.number(),
    reactions: z.number(),
    comments: z.number()
  }),
  features: FeatureStatusesSchema
})

export type SchoolStatsData = z.infer<typeof SchoolStatsDataSchema>

// --- API Request/Response ---

export const ComputeSchoolStatsRequestSchema = z.object({
  period: SchoolStatsPeriodSchema.optional().default('weekly'),
  school_id: z.string().optional(),
  period_start: z.string().optional(),
  dryRun: z.boolean().optional().default(false)
})

export type ComputeSchoolStatsRequest = z.infer<typeof ComputeSchoolStatsRequestSchema>

export const ComputeSchoolStatsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    period: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    schoolsProcessed: z.number(),
    succeeded: z.number(),
    failed: z.number(),
    skipped: z.number(),
    errors: z
      .array(
        z.object({
          schoolId: z.string(),
          error: z.string()
        })
      )
      .optional()
  })
})

export type ComputeSchoolStatsResponse = z.infer<typeof ComputeSchoolStatsResponseSchema>

// --- Get School Stats API (read endpoint for CIO / dashboards) ---

export const GetSchoolStatsRequestSchema = z.object({
  school_id: z.string(),
  period: SchoolStatsPeriodSchema.optional().default('weekly'),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD format')
    .optional()
})

export type GetSchoolStatsRequest = z.infer<typeof GetSchoolStatsRequestSchema>

/** Numeric deltas between current and previous period (no features — those are status enums) */
export const SchoolStatsChangesSchema = z.object({
  addedUsers: UserCountsByRoleSchema,
  activeUsers: UserCountsByRoleSchema,
  pendingUsers: UserCountsByRoleSchema,
  betaTesters: BetaTestersSchema,
  platformContent: z.object({
    published: z.number(),
    reactions: z.number(),
    comments: z.number()
  }),
  userContent: z.object({
    posted: z.number(),
    reactions: z.number(),
    comments: z.number()
  })
})

export type SchoolStatsChanges = z.infer<typeof SchoolStatsChangesSchema>

export const GetSchoolStatsResponseSchema = z.object({
  school: z.object({
    id: z.string(),
    name: z.string()
  }),
  period: SchoolStatsPeriodSchema,
  periodStart: z.string(),
  periodEnd: z.string(),
  stats: SchoolStatsDataSchema,
  changes: SchoolStatsChangesSchema.nullable(),
  hasPreviousPeriod: z.boolean()
})

export type GetSchoolStatsResponse = z.infer<typeof GetSchoolStatsResponseSchema>
